import { LegalPage } from "@/components/LegalPage";
import { CONTACT } from "@/data/content";

const P = ({ children }) => <p className="mb-3 last:mb-0">{children}</p>;
const UL = ({ items }) => (
  <ul className="mb-3 list-disc space-y-1.5 pl-5 last:mb-0">
    {items.map((i, idx) => <li key={idx}>{i}</li>)}
  </ul>
);

const SECTIONS = [
  {
    heading: "Overview",
    body: (
      <P>
        This policy explains whether and when services provided by {CONTACT.name} are refundable, how a client can cancel, and
        what happens to work already completed. It forms part of our Terms & Conditions. Where your written quotation states
        additional terms, those terms apply alongside this policy.
      </P>
    ),
  },
  {
    heading: "How Retainers and Project Milestones Are Billed and Cancelled",
    body: (
      <>
        <P><strong>Project milestones (website design, website development, software and defined business management projects):</strong> projects are billed in milestones stated in the quotation — normally 50% on acceptance (the deposit) and 50% on completion. The deposit is refundable only if work has not started. <strong>Web design and development deposits are non-refundable once project milestones are commenced.</strong> If a project is cancelled mid-way, completed milestones are billed in full and the active milestone is billed proportionally to the work done; any payment above that amount is refunded.</P>
        <P><strong>Professional retainers (website maintenance, SEO/SMO, digital marketing, business operations support):</strong> retainers are billed monthly in advance by B2B invoice. A retainer may be cancelled by written notice at any time; cancellation takes effect at the end of the current billing month and no further months are invoiced. The current month is not refundable once it has begun, because the service is delivered continuously during that month.</P>
        <P><strong>Invoices:</strong> all amounts are billed by custom B2B invoice referencing the quotation, payable within 3 days of the invoice date. Final deliverables are released on receipt of the final milestone payment.</P>
      </>
    ),
  },
  {
    heading: "Standard Refund Framework",
    body: (
      <UL items={[
        <><strong>Full refunds</strong> are available only if work has not started or project resources have not yet been allocated.</>,
        <><strong>Partial refunds</strong> are calculated proportionally for incomplete or active milestones when a project is cancelled mid-way.</>,
        <><strong>No refunds</strong> apply to completed, reviewed and approved development phases, or to delivered source code.</>,
      ]} />
    ),
  },
  {
    heading: "Non-Refundable Items",
    body: (
      <UL items={[
        <><strong>Third-party costs:</strong> domain registrations, server hosting and premium plugin or software licences.</>,
        <><strong>Completed milestones:</strong> delivered wireframes, approved designs, or deployed staging phases.</>,
        <><strong>Client-caused delays:</strong> extended periods of unreturned feedback, or a sudden change of mind after work has begun.</>,
      ]} />
    ),
  },
  {
    heading: "Monthly Services",
    body: (
      <P>
        Monthly services (website maintenance, SEO/SMO, digital marketing, operations support) may be cancelled at any time by
        written notice, with no additional notice period: cancellation takes effect at the end of the current billing month. The
        current month is not refundable once it has started; no further months are charged after cancellation takes effect. Advertising
        spend already paid to Google or social platforms is non-refundable.
      </P>
    ),
  },
  {
    heading: "How to Cancel or Request a Refund",
    body: (
      <P>
        Requests must be submitted in writing to {CONTACT.email} (or by post to {CONTACT.address}) and should include your name,
        company, the service, and the quotation or invoice reference. We confirm every cancellation in writing.
      </P>
    ),
  },
  {
    heading: "Work Already Completed",
    body: (
      <P>
        On cancellation of a project, we hand over all completed and paid-for deliverables in their current state. Work that has
        not been paid for remains our property until payment is received. Deliverables produced during paid monthly periods remain yours.
      </P>
    ),
  },
  {
    heading: "Deposits",
    body: (
      <P>
        Deposits secure your place in our schedule and cover initial project resources. A deposit is refundable in full only if work
        has not started and resources have not been allocated; once work has begun it is treated under the Standard Refund Framework above.
      </P>
    ),
  },
  {
    heading: "Service Not as Described",
    body: (
      <P>
        If a delivered service does not match the description in your accepted quotation, tell us in writing. We will first offer to
        correct the work; if we cannot correct it within a reasonable time, we will refund the fee for the affected part of the work.
      </P>
    ),
  },
  {
    heading: "Processing and Timelines",
    body: (
      <UL items={[
        <><strong>Review window:</strong> written refund requests are usually reviewed within 2 to 7 business days.</>,
        <><strong>Transaction path:</strong> approved amounts are returned via the original payment method (for example credit card or net banking) within 5 to 15 business days of approval.</>,
      ]} />
    ),
  },
  {
    heading: "Contact for Refund Requests",
    body: <P>{CONTACT.name} · {CONTACT.email} · {CONTACT.phone} · {CONTACT.address}, {CONTACT.country}.</P>,
  },
];

export default function RefundPolicy() {
  return (
    <LegalPage
      testId="refund-page"
      overline="Legal — Refund & Cancellation Policy"
      title="Refund & Cancellation Policy"
      lede="When you can cancel, what is refundable, how deposits and completed work are treated, and how refunds are processed."
      seoDescription="Refund and Cancellation Policy for My Geeks LLC services: full, partial and non-refundable cases, non-refundable items, cancellation process and refund timelines."
      sections={SECTIONS}
      updated="October 2, 2026"
    />
  );
}
